import RouteConstants from '@/features/router/RouteConstants';
import { useModalContext } from '@/hooks/useModalContext';
import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { useEmailTemplates } from '../../hooks/useEmailTemplates';
import { EmailTemplate } from '../../types/emailTemplates';
import { DeleteEmailTemplateModal } from '../DeleteEmailTemplateModal';
import { EmailTemplateListContent } from './EmailTemplateListContent';
import { EmailTemplateListControls } from './EmailTemplateListControls';
import { EmailTemplateListEmptyState } from './EmailTemplateListEmptyState';
import { EmailTemplateListErrorState } from './EmailTemplateListErrorState';
import { EmailTemplateListHeader } from './EmailTemplateListHeader';
import { EmailTemplateListLoadingState } from './EmailTemplateListLoadingState';
import { EmailTemplateListStats } from './EmailTemplateListStats';
import { useEmailTemplateListFilters } from './useEmailTemplateListFilters';

export const EmailTemplateList: FC = () => {
  const history = useHistory();
  const { templates, isLoading, isError, reload } = useEmailTemplates();
  const { openModal } = useModalContext();

  const {
    selectedFilter,
    setSelectedFilter,
    viewMode,
    setViewMode,
    searchValue,
    setSearchValue,
    filteredItems,
    templateStats,
    templateFilters,
  } = useEmailTemplateListFilters(templates);

  const handleCreateTemplate = () => {
    history.push(RouteConstants.createEmailTemplate);
  };

  const handleRequestDeleteTemplate = (template: EmailTemplate) => {
    openModal({
      renderContent: DeleteEmailTemplateModal,
      renderContentProps: {
        template: { id: template.id, name: template.name },
      },
      dismissable: true,
      fullWidth: false,
      fullHeight: false,
    });
  };

  const handleEditTemplate = (templateId: string) => {
    history.push(RouteConstants.editEmailTemplate.replace(':templateId', templateId));
  };

  const handleTemplatesHubEdit = (actionType: 'edit' | 'delete') => {
    if (actionType === 'edit') {
      history.push(RouteConstants.emailTemplatesEditHub);
    } else {
      history.push(RouteConstants.emailTemplatesDeleteHub);
    }
  };

  return (
    <section className='space-y-5 rounded-3xl border border-secondary-border bg-surface p-5 shadow-sm md:p-6'>
      <EmailTemplateListHeader onCreate={handleCreateTemplate} />

      {isLoading ? <EmailTemplateListLoadingState /> : null}

      {!isLoading && isError ? <EmailTemplateListErrorState onRetry={reload} /> : null}

      {!isLoading && !isError ? (
        <>
          <EmailTemplateListStats stats={templateStats} />

          {templates.length > 0 ? (
            <EmailTemplateListControls
              templateFilters={templateFilters}
              selectedFilter={selectedFilter}
              onFilterChange={setSelectedFilter}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              filteredCount={filteredItems.length}
              totalCount={templates.length}
              onEditHub={() => handleTemplatesHubEdit('edit')}
              onDeleteHub={() => handleTemplatesHubEdit('delete')}
            />
          ) : null}

          {filteredItems.length > 0 ? (
            <EmailTemplateListContent
              viewMode={viewMode}
              items={filteredItems}
              onEdit={handleEditTemplate}
              onRequestDelete={handleRequestDeleteTemplate}
            />
          ) : (
            <EmailTemplateListEmptyState
              variant={templates.length === 0 ? 'global' : 'filtered'}
              onCreate={templates.length === 0 ? handleCreateTemplate : undefined}
            />
          )}
        </>
      ) : null}
    </section>
  );
};
